import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import AutomatedComplianceChecker from './AutomatedComplianceChecker';
import CopyrightScoringEngine from './CopyrightScoringEngine';
import AutomatedArchivalSystem from './AutomatedArchivalSystem';
import EnhancedDataManagementTools from './EnhancedDataManagementTools';
import { 
  Shield, 
  Scale, 
  Database, 
  AlertTriangle, 
  CheckCircle,
  Archive,
  Copyright,
  FileText,
  TrendingUp
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ComplianceMetrics {
  overall_score: number;
  gdpr_compliance: number;
  copyright_compliance: number;
  data_retention_compliance: number;
  active_violations: number;
  resolved_violations: number;
  pending_requests: number;
  automated_actions_today: number;
}

const LegalComplianceDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState<ComplianceMetrics>({
    overall_score: 0,
    gdpr_compliance: 0,
    copyright_compliance: 0,
    data_retention_compliance: 0,
    active_violations: 0,
    resolved_violations: 0,
    pending_requests: 0,
    automated_actions_today: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ---- Ref and Handler for triggering compliance scan in child ----
  const complianceCheckerRef = useRef<{ runAutomatedCheck: () => void } | null>(null);

  const handleRunComplianceScan = () => {
    setActiveTab('compliance');
    // Defer to allow tab switch
    setTimeout(() => {
      complianceCheckerRef.current?.runAutomatedCheck();
    }, 10);
  };

  // Other quick actions: just set the tab
  const handleActionTab = (tab: string) => setActiveTab(tab);

  // Fetch compliance metrics from Supabase site_analytics
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get recent analytics (assuming metrics updated daily and metric_names are known)
        const sinceDate = new Date();
        sinceDate.setDate(sinceDate.getDate() - 7); // last 7 days
        const { data, error: fetchError } = await supabase
          .from('site_analytics')
          .select('*')
          .gte('metric_date', sinceDate.toISOString().slice(0, 10)); // YYYY-MM-DD

        if (fetchError) throw fetchError;
        if (!data) throw new Error("No analytics data found.");

        // Map metrics. We'll extract latest value by metric_name.
        // If only 1 metric row per name per day, just use the latest date entry per metric.
        const latestMetrics: Record<string, any> = {};
        for (const m of data) {
          latestMetrics[m.metric_name] = m;
        }
        setMetrics({
          overall_score: Number(latestMetrics['overall_compliance']?.metric_value) || 0,
          gdpr_compliance: Number(latestMetrics['gdpr_compliance']?.metric_value) || 0,
          copyright_compliance: Number(latestMetrics['copyright_compliance']?.metric_value) || 0,
          data_retention_compliance: Number(latestMetrics['data_retention_compliance']?.metric_value) || 0,
          active_violations: Number(latestMetrics['active_compliance_violations']?.metric_value) || 0,
          resolved_violations: Number(latestMetrics['resolved_compliance_violations']?.metric_value) || 0,
          pending_requests: Number(latestMetrics['pending_data_requests']?.metric_value) || 0,
          automated_actions_today: Number(latestMetrics['automated_actions_today']?.metric_value) || 0,
        });
      } catch (err: any) {
        setError(err.message || String(err));
      }
      setLoading(false);
    };
    fetchMetrics();
  }, []);

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-black';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-white';
    if (score >= 70) return 'bg-white';
    return 'bg-red-100';
  };

  return (
    <div className="space-y-6">
      {/* Add extra top padding for layout consistency */}
      <div className="pt-6" />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Legal & Compliance Automation Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="compliance">Compliance</TabsTrigger>
              <TabsTrigger value="copyright">Copyright</TabsTrigger>
              <TabsTrigger value="archival">Archival</TabsTrigger>
              <TabsTrigger value="data-mgmt">Data Mgmt</TabsTrigger>
              <TabsTrigger value="automation">Automation</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {loading ? (
                <div className="text-center py-4">Loading compliance metrics…</div>
              ) : error ? (
                <div className="text-center py-4 text-red-700">Error loading metrics: {error}</div>
              ) : (
                <>
                  {/* Overall Compliance Dashboard */}
                  <div className="grid gap-4 md:grid-cols-4">
                    <Card className={`${getScoreBg(metrics.overall_score)}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Overall Compliance</p>
                            <p className={`text-2xl font-bold ${getScoreColor(metrics.overall_score)}`}>
                              {metrics.overall_score}%
                            </p>
                          </div>
                          <Shield className={`h-8 w-8 ${getScoreColor(metrics.overall_score)}`} />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className={`${getScoreBg(metrics.gdpr_compliance)}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">GDPR Compliance</p>
                            <p className={`text-2xl font-bold ${getScoreColor(metrics.gdpr_compliance)}`}>
                              {metrics.gdpr_compliance}%
                            </p>
                          </div>
                          <Database className={`h-8 w-8 ${getScoreColor(metrics.gdpr_compliance)}`} />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className={`${getScoreBg(metrics.copyright_compliance)}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Copyright</p>
                            <p className={`text-2xl font-bold ${getScoreColor(metrics.copyright_compliance)}`}>
                              {metrics.copyright_compliance}%
                            </p>
                          </div>
                          <Copyright className={`h-8 w-8 ${getScoreColor(metrics.copyright_compliance)}`} />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className={`${getScoreBg(metrics.data_retention_compliance)}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Data Retention</p>
                            <p className={`text-2xl font-bold ${getScoreColor(metrics.data_retention_compliance)}`}>
                              {metrics.data_retention_compliance}%
                            </p>
                          </div>
                          <Archive className={`h-8 w-8 ${getScoreColor(metrics.data_retention_compliance)}`} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Active Violations</p>
                            <p className="text-2xl font-bold text-red-600">{metrics.active_violations}</p>
                          </div>
                          <AlertTriangle className="h-6 w-6 text-red-600" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Resolved Violations</p>
                            <p className="text-2xl font-bold text-green-600">{metrics.resolved_violations}</p>
                          </div>
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Pending Requests</p>
                            <p className="text-2xl font-bold text-black">{metrics.pending_requests}</p>
                          </div>
                          <FileText className="h-6 w-6 text-black" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-black">Auto Actions Today</p>
                            <p className="text-2xl font-bold text-black">{metrics.automated_actions_today}</p>
                          </div>
                          <TrendingUp className="h-6 w-6 text-black" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Button className="h-20 flex-col gap-2"
                          onClick={handleRunComplianceScan}>
                          <Shield className="h-6 w-6" />
                          Run Compliance Scan
                        </Button>
                        <Button variant="outline" className="h-20 flex-col gap-2"
                          onClick={() => handleActionTab('copyright')}>
                          <Copyright className="h-6 w-6" />
                          Check Copyright
                        </Button>
                        <Button variant="outline" className="h-20 flex-col gap-2"
                          onClick={() => handleActionTab('archival')}>
                          <Archive className="h-6 w-6" />
                          Review Archival
                        </Button>
                        <Button variant="outline" className="h-20 flex-col gap-2"
                          onClick={() => handleActionTab('data-mgmt')}>
                          <Database className="h-6 w-6" />
                          Process Data Requests
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Activity */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Compliance Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <div className="flex-1">
                            <p className="font-medium">Copyright issue resolved</p>
                            <p className="text-sm text-black">
                              Attribution added to "Philosophy of Ethics" content
                            </p>
                          </div>
                          <Badge className="bg-white text-green-800">Automated</Badge>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                          <Database className="h-5 w-5 text-black" />
                          <div className="flex-1">
                            <p className="font-medium">Data export completed</p>
                            <p className="text-sm text-black">
                              User data package prepared for user@example.com
                            </p>
                          </div>
                          <Badge className="bg-white text-black">Manual</Badge>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-black" />
                          <div className="flex-1">
                            <p className="font-medium">Content flagged for review</p>
                            <p className="text-sm text-black">
                              "Historical Analysis" requires copyright verification
                            </p>
                          </div>
                          <Badge className="bg-white text-black">Automated</Badge>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-white rounded-lg">
                          <Archive className="h-5 w-5 text-black" />
                          <div className="flex-1">
                            <p className="font-medium">Content auto-archived</p>
                            <p className="text-sm text-black">
                              5 items archived due to age-based retention policy
                            </p>
                          </div>
                          <Badge className="bg-white text-black">Scheduled</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Compliance Alerts */}
                  {metrics.active_violations > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{metrics.active_violations} active compliance violations</strong> require attention. 
                        Review the compliance tab for details and recommended actions.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="compliance">
              <AutomatedComplianceChecker ref={complianceCheckerRef} />
            </TabsContent>

            <TabsContent value="copyright">
              <CopyrightScoringEngine />
            </TabsContent>

            <TabsContent value="archival">
              <AutomatedArchivalSystem />
            </TabsContent>

            <TabsContent value="data-mgmt">
              <EnhancedDataManagementTools />
            </TabsContent>

            <TabsContent value="automation">
              <Card>
                <CardHeader>
                  <CardTitle>Automation Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        Configure automated compliance and legal processes to ensure continuous adherence to regulations.
                      </AlertDescription>
                    </Alert>

                    <div className="grid gap-4 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <CheckCircle className="h-5 w-5" />
                            Automated Compliance Scanning
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-black mb-3">
                            Automatically scan content for GDPR, copyright, and ToS compliance issues.
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Daily Scans</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Real-time Monitoring</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Auto-flagging</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Archive className="h-5 w-5" />
                            Smart Archival System
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-black mb-3">
                            Automatically archive or lock content based on compliance rules and retention policies.
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Age-based Archival</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Compliance-based Locking</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Legal Flag Response</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Database className="h-5 w-5" />
                            GDPR Automation
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-black mb-3">
                            Automate GDPR compliance tasks including data exports, deletions, and consent management.
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Auto Data Exports</span>
                              <Badge className="bg-white text-black">Partial</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Consent Monitoring</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Data Retention</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Copyright className="h-5 w-5" />
                            Copyright Protection
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-black mb-3">
                            Automatically detect and score copyright compliance, suggest improvements.
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Citation Checking</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Attribution Validation</span>
                              <Badge className="bg-white text-green-800">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm">Fair Use Analysis</span>
                              <Badge className="bg-white text-black">Beta</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalComplianceDashboard;
